import { useState, useMemo } from "react";
import {
  FiPlus, FiTrash2, FiRefreshCw, FiSearch,
  FiX, FiZap, FiExternalLink,
} from "react-icons/fi";
import { FaYoutube } from "react-icons/fa";
import StatusBadge from "../../components/common/StatusBadge";
import Pagination from "../../components/common/Pagination";
import toast from "react-hot-toast";

const MOCK_VIDEOS = [
  { id: 1, url: "https://youtube.com/watch?v=dQw4w9WgXcQ", title: "Product Demo Walkthrough",   duration: "3:42", status: "Trained",  added: "3 days ago" },
  { id: 2, url: "https://youtube.com/watch?v=9bZkp7q19f0", title: "Getting Started Guide",       duration: "4:15", status: "Trained",  added: "1 week ago" },
  { id: 3, url: "https://youtube.com/watch?v=l482T0yNkeo", title: "Feature Overview",            duration: "5:30", status: "Pending",  added: "1 day ago" },
  { id: 4, url: "https://youtube.com/watch?v=YQHsXMglC9A", title: "Dashboard & Analytics Tour", duration: "6:08", status: "Trained",  added: "4 days ago" },
  { id: 5, url: "https://youtube.com/watch?v=kJQP7kiw5Fk", title: "Integrations Walkthrough",   duration: "7:22", status: "Failed",   added: "5 days ago" },
];

const PAGE_SIZE = 8;

function extractVideoId(url) {
  const match = url.match(/(?:v=|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return match?.[1] || null;
}

export default function YoutubeContent() {
  const [videos, setVideos] = useState(MOCK_VIDEOS);
  const [selectedIds, setSelectedIds] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [showAdd, setShowAdd] = useState(false);
  const [input, setInput] = useState("");

  /* ── Derived ── */
  const counts = useMemo(() => ({
    total:   videos.length,
    trained: videos.filter(v => v.status === "Trained").length,
    pending: videos.filter(v => v.status === "Pending").length,
    failed:  videos.filter(v => v.status === "Failed").length,
  }), [videos]);

  const filtered = useMemo(() => videos.filter(v => {
    const okFilter = filter === "all" || v.status === filter;
    const okSearch = !search || v.title.toLowerCase().includes(search.toLowerCase()) || v.url.toLowerCase().includes(search.toLowerCase());
    return okFilter && okSearch;
  }), [videos, filter, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const pageIds    = paginated.map(v => v.id);
  const allPageSel = pageIds.length > 0 && pageIds.every(id => selectedIds.includes(id));

  const changeFilter = (f) => { setFilter(f); setPage(1); setSelectedIds([]); };
  const changeSearch = (s) => { setSearch(s); setPage(1); };
  const toggleSel    = (id) => setSelectedIds(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const toggleAllSel = () => {
    if (allPageSel) setSelectedIds(p => p.filter(id => !pageIds.includes(id)));
    else setSelectedIds(p => [...new Set([...p, ...pageIds])]);
  };

  const simulateRetrain = (ids, cb) => {
    setVideos(p => p.map(v => ids.includes(v.id) ? { ...v, status: "Processing" } : v));
    setTimeout(() => {
      setVideos(p => p.map(v => ids.includes(v.id) ? { ...v, status: "Trained", added: "Just now" } : v));
      cb?.();
    }, 2000);
  };

  /* ── Handlers ── */
  const handleAdd = () => {
    const url = input.trim();
    if (!url || (!url.includes("youtube.com") && !url.includes("youtu.be"))) {
      toast.error("Please enter a valid YouTube or youtu.be URL."); return;
    }
    if (videos.some(v => v.url === url)) { toast.error("This video is already added."); return; }
    const newVideo = { id: Date.now(), url, title: "Processing…", duration: "—", status: "Pending", added: "Just now" };
    setVideos(p => [newVideo, ...p]);
    setInput(""); setShowAdd(false); setPage(1);
    toast.success("YouTube video added and queued for training.");
  };

  const handleDelete = (id) => {
    setVideos(p => p.filter(v => v.id !== id));
    setSelectedIds(p => p.filter(x => x !== id));
    toast.success("Video removed.");
  };

  const handleRetrain = (id) => simulateRetrain([id], () => toast.success("Video retrained."));

  const handleBulkDelete = () => {
    const n = selectedIds.length;
    setVideos(p => p.filter(v => !selectedIds.includes(v.id)));
    setSelectedIds([]); toast.success(`${n} video${n > 1 ? "s" : ""} deleted.`);
  };

  const handleBulkRetrain = () => {
    const ids = [...selectedIds];
    simulateRetrain(ids, () => { setSelectedIds([]); toast.success(`${ids.length} video${ids.length > 1 ? "s" : ""} retrained.`); });
  };

  const handleTrainAllPending = () => {
    const ids = videos.filter(v => v.status === "Pending").map(v => v.id);
    if (!ids.length) return;
    simulateRetrain(ids, () => toast.success(`${ids.length} pending video${ids.length > 1 ? "s" : ""} trained.`));
  };

  return (
    <div className="-m-6 flex flex-col" style={{ height: "calc(100vh - 4rem)" }}>

      {/* ── Sticky header ── */}
      <div className="shrink-0 bg-white border-b px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FaYoutube size={22} className="text-red-500 shrink-0" />
          <div>
            <h1 className="text-xl font-semibold text-gray-900">YouTube Videos</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {counts.total} videos · {counts.trained} trained · transcripts indexed
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {counts.pending > 0 && (
            <button onClick={handleTrainAllPending} className="flex items-center gap-2 text-sm border border-primary text-primary px-4 py-2 rounded-lg hover:bg-primary/5 transition">
              <FiZap size={14} /> Train {counts.pending} Pending
            </button>
          )}
          <button
            onClick={() => setShowAdd(!showAdd)}
            className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg text-sm hover:opacity-90 transition"
          >
            {showAdd ? <FiX size={14} /> : <FiPlus size={14} />}
            {showAdd ? "Cancel" : "Add Video"}
          </button>
        </div>
      </div>

      {/* ── Scrollable body ── */}
      <div className="flex-1 overflow-y-auto bg-gray-50">

        {/* Add panel */}
        {showAdd && (
          <div className="bg-white border-b shadow-sm">
            <div className="px-6 py-5 space-y-2 max-w-2xl">
              <p className="text-sm text-gray-500">NexaGPT extracts the video transcript and trains your chatbot on it.</p>
              <div className="flex gap-3">
                <input
                  type="url" value={input} onChange={e => setInput(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleAdd()}
                  placeholder="https://youtube.com/watch?v=… or https://youtu.be/…"
                  className="flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  autoFocus
                />
                <button onClick={handleAdd} className="px-4 py-2 bg-primary text-white rounded-lg text-sm hover:opacity-90">
                  Add Video
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="p-6 space-y-4">

          {/* KPI cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <KpiCard label="Total Videos" value={counts.total}   color="text-gray-900" />
            <KpiCard label="Trained"      value={counts.trained} color="text-green-600" dot="bg-green-500" />
            <KpiCard label="Pending / Failed" value={`${counts.pending} / ${counts.failed}`} color="text-yellow-600" dot="bg-yellow-500" />
            <KpiCard label="Transcripts"  value={counts.trained} color="text-red-500" />
          </div>

          {/* Search + filter */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="relative flex-1 max-w-sm">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
              <input
                type="text" value={search} onChange={e => changeSearch(e.target.value)}
                placeholder="Search videos…"
                className="w-full pl-9 pr-8 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 bg-white"
              />
              {search && <button onClick={() => changeSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><FiX size={13} /></button>}
            </div>
            <div className="flex items-center border rounded-lg overflow-hidden text-xs font-medium shrink-0">
              {["all", "Trained", "Pending", "Failed"].map(f => (
                <button key={f} onClick={() => changeFilter(f)} className={`px-3 py-1.5 transition ${filter === f ? "bg-primary text-white" : "text-gray-500 hover:bg-gray-50"}`}>
                  {f === "all" ? "All" : f}
                </button>
              ))}
            </div>
          </div>

          {/* Bulk bar */}
          {selectedIds.length > 0 && (
            <div className="flex items-center gap-3 bg-primary/5 border border-primary/20 rounded-xl px-4 py-2.5">
              <span className="text-sm font-medium text-primary">{selectedIds.length} selected</span>
              <div className="flex gap-2 ml-auto">
                <button onClick={handleBulkRetrain} className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-gray-200 bg-white text-gray-700 rounded-lg hover:bg-gray-50">
                  <FiRefreshCw size={12} /> Retrain Selected
                </button>
                <button onClick={handleBulkDelete} className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-red-100 text-red-600 rounded-lg hover:bg-red-200">
                  <FiTrash2 size={12} /> Delete Selected
                </button>
                <button onClick={() => setSelectedIds([])} className="text-gray-400 hover:text-gray-600 p-1"><FiX size={14} /></button>
              </div>
            </div>
          )}

          {/* Table */}
          <div className="bg-white border rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b text-xs text-gray-500 uppercase tracking-wide">
                  <tr>
                    <th className="w-10 pl-4 py-3">
                      <input type="checkbox" checked={allPageSel} onChange={toggleAllSel} className="accent-primary" />
                    </th>
                    <th className="px-3 py-3 text-left font-semibold">Video</th>
                    <th className="py-3 text-left font-semibold">Duration</th>
                    <th className="py-3 text-left font-semibold">Status</th>
                    <th className="py-3 text-left font-semibold">Added</th>
                    <th className="py-3 pr-4 text-right font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {paginated.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-16">
                        <FaYoutube size={30} className="mx-auto mb-2 text-gray-300" />
                        <p className="text-sm text-gray-500">
                          {search || filter !== "all" ? "No videos match your filters." : "No videos added yet."}
                        </p>
                        {!search && filter === "all" && (
                          <button onClick={() => setShowAdd(true)} className="mt-3 text-sm text-primary hover:underline">
                            Add your first video
                          </button>
                        )}
                      </td>
                    </tr>
                  ) : paginated.map(v => {
                    const vid = extractVideoId(v.url);
                    return (
                      <tr key={v.id} className={`hover:bg-gray-50 transition ${selectedIds.includes(v.id) ? "bg-primary/5" : ""}`}>
                        <td className="pl-4 py-3">
                          <input type="checkbox" checked={selectedIds.includes(v.id)} onChange={() => toggleSel(v.id)} className="accent-primary" />
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-3">
                            {/* Thumbnail */}
                            <div className="w-16 h-10 rounded-lg overflow-hidden bg-gray-100 shrink-0 flex items-center justify-center">
                              {vid ? (
                                <img
                                  src={`https://img.youtube.com/vi/${vid}/mqdefault.jpg`}
                                  alt=""
                                  className="w-full h-full object-cover"
                                  onError={e => { e.target.style.display = "none"; }}
                                />
                              ) : (
                                <FaYoutube size={18} className="text-red-400" />
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-gray-800 truncate max-w-[200px]">{v.title}</p>
                              <a
                                href={v.url} target="_blank" rel="noreferrer"
                                className="text-xs text-blue-500 hover:underline flex items-center gap-0.5 truncate max-w-[200px]"
                              >
                                <FiExternalLink size={10} /> {v.url.replace("https://", "")}
                              </a>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 pr-3 text-gray-500 text-xs tabular-nums">{v.duration}</td>
                        <td className="py-3 pr-3"><StatusBadge status={v.status} /></td>
                        <td className="py-3 pr-3 text-gray-500 text-xs whitespace-nowrap">{v.added}</td>
                        <td className="py-3 pr-4">
                          <div className="flex justify-end gap-2">
                            {v.status === "Processing" ? (
                              <span className="flex items-center gap-1.5 text-xs text-blue-600 px-3 py-1.5">
                                <span className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                                Training…
                              </span>
                            ) : (
                              <>
                                <button onClick={() => handleRetrain(v.id)} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition">
                                  <FiRefreshCw size={12} /> Retrain
                                </button>
                                <button onClick={() => handleDelete(v.id)} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition">
                                  <FiTrash2 size={12} /> Delete
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination footer */}
          {filtered.length > PAGE_SIZE && (
            <div className="flex items-center justify-between text-sm text-gray-500">
              <p>Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length} videos</p>
              <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, color, dot }) {
  return (
    <div className="bg-white border rounded-xl px-4 py-3">
      <div className="flex items-center gap-1.5 mb-1">
        {dot && <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dot}`} />}
        <p className="text-xs text-gray-500 font-medium truncate">{label}</p>
      </div>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
    </div>
  );
}
